import { isNative, platform } from '../native/platform.js';
import { toast } from '../runtime/legacy.js';
import { SUPPORT_EMAIL } from '../config/env.js';

/**
 * Purchase routing for the store builds.
 *
 * The legacy core sends every subscription and token-bundle purchase to Stripe
 * Checkout by navigating the web view to an external URL. On the web that is
 * fine. In a store build it is not:
 *
 *   • App Store Review Guideline 3.1.1 — unlocking features or content inside
 *     an app requires In-App Purchase. Linking out to a web checkout for the
 *     same thing is a rejection, and after review it is an account-level issue.
 *   • Google Play Payments policy — the same rule, with a narrower set of
 *     exemptions that this app does not fall into (tokens and premium features
 *     are consumed in-app).
 *
 * So on native, web checkout is blocked at the point of click. The provider
 * interface below is where StoreKit / Google Play Billing gets wired in —
 * `registerPurchaseProvider()` is the only thing that needs to change once a
 * billing plugin is installed and products are configured in App Store Connect
 * and the Play Console. Until then the buttons explain themselves instead of
 * failing silently or, worse, succeeding through the wrong channel.
 *
 * See docs/STORE_SUBMISSION.md for the full wiring checklist.
 */

/**
 * @typedef {object} PurchaseProvider
 * @property {(productId: string) => Promise<{ success: boolean }>} purchase
 * @property {() => Promise<void>} restore
 */

/** @type {PurchaseProvider | null} */
let provider = null;

/** Installs the platform billing implementation. See docs/STORE_SUBMISSION.md. */
export function registerPurchaseProvider(impl) {
  provider = impl;
}

export function hasPurchaseProvider() {
  return provider !== null;
}

/** Maps the app's plan/bundle ids onto store product identifiers. */
export const PRODUCT_IDS = {
  subscription: {
    weekly: 'ai.sigro.ironpact.sub.weekly',
    monthly: 'ai.sigro.ironpact.sub.monthly',
    quarterly: 'ai.sigro.ironpact.sub.quarterly',
    yearly: 'ai.sigro.ironpact.sub.yearly',
  },
  tokens: {
    starter: 'ai.sigro.ironpact.tokens.starter',
    power: 'ai.sigro.ironpact.tokens.power',
    elite: 'ai.sigro.ironpact.tokens.elite',
    champion: 'ai.sigro.ironpact.tokens.champion',
    legendary: 'ai.sigro.ironpact.tokens.legendary',
  },
};

const STORE_NAME = platform === 'ios' ? 'the App Store' : 'Google Play';

function describeProduct(button) {
  const plan = button.getAttribute('data-plan');
  if (plan) return { kind: 'subscription', id: plan };

  const bundle = button.dataset.bundle;
  if (bundle) return { kind: 'tokens', id: bundle };

  return null;
}

async function handleNativePurchase(event, button) {
  const product = describeProduct(button);
  if (!product) return;

  // Stop the core's own click handler from starting a Stripe session.
  event.preventDefault();
  event.stopImmediatePropagation();

  if (!provider) {
    toast(
      `In-app purchases through ${STORE_NAME} are not available in this build yet. ` +
        `Email ${SUPPORT_EMAIL} if you need help with your subscription.`,
      'info',
      6000,
    );
    return;
  }

  const productId = PRODUCT_IDS[product.kind]?.[product.id];
  if (!productId) {
    console.error('[ironpact] no store product mapped for', product);
    toast('That purchase is unavailable right now.', 'error');
    return;
  }

  const label = button.textContent;
  button.disabled = true;
  button.textContent = 'Processing…';
  try {
    const result = await provider.purchase(productId);
    if (result?.success) {
      toast('Purchase complete!', 'success');
      // The billing webhook is what actually credits the account; re-reading
      // is how the UI picks that up.
      await globalThis.sbLoadSubscription?.();
      await globalThis.sbLoadTokenBalance?.();
      globalThis.updateTokenDisplay?.();
    }
  } catch (err) {
    console.error('[ironpact] purchase failed', err);
    toast(`Purchase failed: ${err.message || err}`, 'error', 5000);
  } finally {
    button.disabled = false;
    button.textContent = label;
  }
}

/**
 * "Restore purchases" is mandatory on iOS for any app selling non-consumables
 * or subscriptions (Guideline 3.1.1) — without it, a user reinstalling the app
 * has no way back to what they paid for.
 */
function addRestoreButton(container) {
  if (!isNative || container.querySelector('#restore-purchases-btn')) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.id = 'restore-purchases-btn';
  button.className = 'btn btn-secondary w-full';
  button.style.marginTop = '16px';
  button.textContent = 'Restore purchases';

  button.addEventListener('click', async () => {
    if (!provider) {
      toast('Restoring purchases is not available in this build yet.', 'info');
      return;
    }
    button.disabled = true;
    try {
      await provider.restore();
      await globalThis.sbLoadSubscription?.();
      toast('Purchases restored', 'success');
    } catch (err) {
      toast(`Could not restore purchases: ${err.message || err}`, 'error');
    } finally {
      button.disabled = false;
    }
  });

  container.appendChild(button);
}

export function installPurchases() {
  if (!isNative) return;

  document.addEventListener(
    'click',
    (event) => {
      const button =
        event.target instanceof Element &&
        event.target.closest('.subscribe-btn, .token-bundle-btn');
      if (button) handleNativePurchase(event, button);
    },
    // Capture, so this runs before the listeners the core binds to the button.
    true,
  );

  // Attach "Restore purchases" wherever the subscription plans are shown.
  const observer = new MutationObserver(() => {
    const container = document.getElementById('view-container');
    if (container?.querySelector('.plans-grid')) addRestoreButton(container);
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
